/**
 * Предупреждение о необходимости выставить счёт — `01-BUSINESS-RULES.md` §2.8.
 *
 * Это ЧИСТО ВЫЧИСЛЯЕМОЕ состояние по текущим данным. Его нельзя хранить в
 * таблице и нельзя заводить под него отдельный процесс или задачу: как только
 * счёт выставлен, признак обязан исчезнуть сам, без фонового job'а, который
 * рано или поздно отстанет от реальности.
 */
import { legalOf } from './pricing.js';
/**
 * Нужно ли показать предупреждающий значок вместо прочерка на месте номера счёта.
 *
 * Все четыре условия обязаны выполняться ОДНОВРЕМЕННО:
 *   1. заказ в статусе «Готово»;
 *   2. способ оплаты заказа — «По договору» (наличные оплачиваются на месте,
 *      счёт для них не выставляется);
 *   3. у юрлица этого заказа включён флаг `warnOnReady` и НЕ включено
 *      «сводить в одну строку»: такие клиники платят за месяц целиком, счёт
 *      им выставляют разом в конце месяца, и значок у каждого готового заказа
 *      был бы напоминанием о том, чего делать не нужно;
 *   4. заказ ещё не включён ни в один счёт.
 *
 * Флаг живёт на ЮРЛИЦЕ, а не на клинике и не на филиале — там же, где способ
 * оплаты по умолчанию. У одной клиники может быть два юрлица с разными
 * требованиями к документообороту.
 */
export function needsInvoiceWarning(order, clinics) {
    if (order.status !== 'Готово')
        return false;
    if (order.pay !== 'По договору')
        return false;
    if (order.invoiceId !== null)
        return false;
    /*
      Заказ с бумажным счётом из прежнего учёта закрыт так же, как заказ со
      счётом в программе: работа выставлена, просто документ жил в другом месте.
      Без этой проверки весь перенесённый июль встал бы с предупреждением
      «нужно оформить счёт» — и указывало бы оно на работу, которую делать
      не нужно.
    */
    if (order.legacyInvoiceNo != null)
        return false;
    const legal = legalOf(clinics, order.clinicId, order.branchId);
    return legal?.warnOnReady === true && legal.mergeInvoiceItems !== true;
}
/** Сколько заказов ждут выставления счёта — для сводного индикатора в интерфейсе. */
export function countOrdersNeedingInvoice(orders, clinics) {
    return orders.reduce((count, order) => (needsInvoiceWarning(order, clinics) ? count + 1 : count), 0);
}
/**
 * Можно ли включить заказ в счёт.
 *
 * Правило из демо: в счёт попадают только заказы по договору, ещё не включённые
 * ни в один счёт. Наличные заказы при выделении не блокируются жёстко, но
 * интерфейс обязан предупредить, что они не попадут в документ.
 *
 * Заказ БЕЗ КЛИНИКИ не попадает в счёт никогда: счёт выставляется на юрлицо,
 * а юрлицо определяется филиалом клиники. Такой заказ частный — пациент
 * пришёл сам. Сервер это тоже проверяет; здесь — чтобы интерфейс не предлагал
 * заведомо невозможное.
 */
export function isInvoiceable(order) {
    if (!order.clinicId || !order.branchId)
        return false;
    // Бумажный счёт из прежнего учёта — тоже счёт: второй раз работу не выставляют.
    if (order.legacyInvoiceNo != null)
        return false;
    return order.pay === 'По договору' && order.invoiceId === null;
}
//# sourceMappingURL=warn-on-ready.js.map